import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/Edit";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";

import { formatCurrencyValue } from "src/utils/formatter";
import { bankAccountTransactionsAndBalanceSelector } from "src/store/bankAccount/selector";
import type { Reconciliation } from "src/interfaces/reconciliation.interface";
import { StatefulLoadableIconButton } from "src/components/LoadableIconButton";
import { useRecordEvent } from "src/hooks/useRecordEvent";
import {
  effectiveUserTimeZoneSelector,
  userRoleSelector,
} from "src/store/system/selector";
import {
  tryYmdStringToEndOfDayUtc,
  tryYmdStringToStartOfDayUtc,
} from "src/utils/timezone";
import type { BankAccountWithReconciliations } from "./types";

import Lamp from "./Lamp";
import BeggningBalanceDialog from "./BeginningBalanceDialog";
import EditBegginnigBalanceDialog from "./EditBeginningBalanceDialog";
import AddReconciliationDialog from "./AddReconciliationDialog";
import ManualReconciliationDialog from "./ManualReconciliationDialog";
import EditReconciliationDialog from "./EditReconciliationDialog";

const useSeparatorStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    height: 2,
    backgroundColor: theme.palette.action.active,
    maxWidth: 80,
    flexShrink: 0,
  },
}));

function Separator() {
  const classes = useSeparatorStyles();
  return <div className={classes.root} />;
}

const useLampContainerStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    marginTop: theme.spacing(2),
    padding: theme.spacing(1),
    minHeight: 50,
    flexWrap: "nowrap",
    overflowX: "auto",
    overflowY: "hidden",
    cursor: "grab",
    userSelect: "none",
    "&:active": {
      cursor: "grabbing",
    },
  },
}));

interface LampContainerProps {
  children: React.ReactNode[];
}

function LampContainer({ children }: LampContainerProps) {
  const classes = useLampContainerStyles();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const hasMovedRef = useRef(false);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft =
        scrollContainerRef.current.scrollWidth;
    }
  }, [children.length]);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!scrollContainerRef.current) return;

    const target = e.target as HTMLElement;
    if (
      target.tagName === "BUTTON" ||
      target.closest("button") ||
      target.closest("[role='button']")
    ) {
      return;
    }
    isDraggingRef.current = true;
    hasMovedRef.current = false;
    startXRef.current = e.pageX - scrollContainerRef.current.offsetLeft;
    scrollLeftRef.current = scrollContainerRef.current.scrollLeft;
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current || !scrollContainerRef.current) return;
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = x - startXRef.current;

    if (!hasMovedRef.current && Math.abs(walk) < 5) {
      return;
    }

    hasMovedRef.current = true;
    e.preventDefault();
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.cursor = "grabbing";
    }
    const scrollWalk = walk * 2; // Scroll speed multiplier
    scrollContainerRef.current.scrollLeft = scrollLeftRef.current - scrollWalk;
  };

  const handleMouseUp = () => {
    if (!scrollContainerRef.current) return;
    isDraggingRef.current = false;
    hasMovedRef.current = false;
    scrollContainerRef.current.style.cursor = "grab";
  };

  const handleMouseLeave = () => {
    if (!scrollContainerRef.current) return;
    isDraggingRef.current = false;
    hasMovedRef.current = false;
    scrollContainerRef.current.style.cursor = "grab";
  };

  return (
    <div
      ref={scrollContainerRef}
      className={classes.container}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </div>
  );
}

const useStyles = makeStyles({
  actions: {
    justifyContent: "flex-end",
  },
  icon: {
    width: "100%",
    height: "100%",
  },
  wrapper: {
    flexShrink: 0,
  },
});

interface BankAccountProps {
  bankAccount: BankAccountWithReconciliations;
  onEdit?: () => void;
  onVisibility?: () => Promise<void>;
  onReconciliationUpdated: (reconciliation: Reconciliation) => void;
}

function BankAccount({
  bankAccount,
  onEdit,
  onVisibility,
  onReconciliationUpdated,
}: BankAccountProps) {
  const recordEvent = useRecordEvent();

  const classes = useStyles();
  const isViewer = useSelector(userRoleSelector) === "viewer";
  const timeZone = useSelector(effectiveUserTimeZoneSelector);
  const [dialog, setDialog] = useState<
    | null
    | { type: "beginning-balance" }
    | { type: "edit-beginning-balance" }
    | { type: "add-reconciliation" }
    | { type: "manual-reconciliation"; index?: number }
    | { type: "edit-reconciliation"; index: number }
  >(null);
  const previousBankAccount = useRef(bankAccount);
  useEffect(
    () => () => {
      previousBankAccount.current = bankAccount;
    },
    [bankAccount]
  );
  const balance = useSelector((state) => {
    if (bankAccount.reconciliations.length <= 0) return 0;
    let statementOfTruth = bankAccount.reconciliations[0];
    for (let i = 0; i < bankAccount.reconciliations.length; i++) {
      const reconciliation = bankAccount.reconciliations[i];
      if (reconciliation.open) break;
      statementOfTruth = reconciliation;
    }
    const statementCutoff =
      tryYmdStringToEndOfDayUtc(statementOfTruth.date, timeZone) ??
      tryYmdStringToStartOfDayUtc(statementOfTruth.date, timeZone) ??
      new Date(0);
    return (
      statementOfTruth.balance +
      bankAccountTransactionsAndBalanceSelector(state, {
        id: bankAccount.id,
        start: statementCutoff,
      }).balance
    );
  });
  const openReconciliationIndex = bankAccount.reconciliations.findIndex(
    (ba) => ba.open
  );

  return (
    <>
      <Card variant="outlined">
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center">
              <Typography variant="h6">{bankAccount.name}</Typography>
              {onEdit ? (
                <Box ml={1}>
                  <IconButton size="small" onClick={onEdit} disabled={isViewer}>
                    <EditIcon fontSize="inherit" />
                  </IconButton>
                </Box>
              ) : null}
              {onVisibility ? (
                <Box ml={1}>
                  <StatefulLoadableIconButton
                    size="small"
                    onClick={onVisibility}
                    disabled={isViewer}
                  >
                    {bankAccount.hidden ? (
                      <VisibilityOffIcon fontSize="inherit" />
                    ) : (
                      <VisibilityIcon fontSize="inherit" />
                    )}
                  </StatefulLoadableIconButton>
                </Box>
              ) : null}
            </Box>
            <Typography variant="body2">
              Balance:{" "}
              {bankAccount.reconciliations.length === 0
                ? "Unreconciled"
                : formatCurrencyValue(balance)}
            </Typography>
          </Box>
          <LampContainer
            children={bankAccount.reconciliations.map((r, i) => (
              <React.Fragment key={r.id}>
                {i > 0 ? <Separator /> : null}
                <div className={classes.wrapper}>
                  <Lamp
                    reconciliation={r}
                    timeZone={timeZone}
                    position={i > 0 ? "Ending" : "Beginning"}
                    onClick={
                      isViewer
                        ? undefined
                        : () => {
                            if (i === 0) {
                              setDialog({ type: "edit-beginning-balance" });
                              return;
                            }
                            setDialog({
                              type: "edit-reconciliation",
                              index: i,
                            });
                          }
                    }
                    initialAnimate={
                      i > 0 &&
                      !r.open &&
                      !previousBankAccount.current?.reconciliations?.[i]
                    }
                  />
                </div>
              </React.Fragment>
            ))}
          />
        </CardContent>
        <CardActions className={classes.actions}>
          {bankAccount.reconciliations.length === 0 ? (
            <Button
              color="primary"
              variant="contained"
              disabled={isViewer}
              onClick={() => {
                recordEvent({
                  type: "feature-adoption",
                  feature: "bank-reconciliation-started",
                  element: "start-reconciling",
                });
                setDialog({ type: "beginning-balance" });
              }}
            >
              Start Reconciling
            </Button>
          ) : openReconciliationIndex > -1 ? (
            <Button
              color="primary"
              variant="contained"
              disabled={isViewer}
              onClick={() => {
                recordEvent({
                  type: "feature-adoption",
                  feature: "bank-reconciliation-started",
                  element: "reconcile",
                });
                setDialog({
                  type: "manual-reconciliation",
                  index: openReconciliationIndex,
                });
              }}
            >
              Reconcile
            </Button>
          ) : (
            <Button
              color="primary"
              variant="contained"
              disabled={isViewer}
              onClick={() => {
                recordEvent({
                  type: "feature-adoption",
                  feature: "bank-reconciliation-started",
                  element: "reconcile-new",
                });
                setDialog({ type: "add-reconciliation" });
              }}
            >
              Reconcile New
            </Button>
          )}
        </CardActions>
      </Card>
      <BeggningBalanceDialog
        open={dialog?.type === "beginning-balance"}
        onClose={() => setDialog(null)}
        bankAccountId={bankAccount.id}
        account={bankAccount.name}
        onReconciliationUpdated={onReconciliationUpdated}
      />
      <EditBegginnigBalanceDialog
        open={dialog?.type === "edit-beginning-balance"}
        onClose={() => setDialog(null)}
        bankAccount={bankAccount}
        onReconciliationUpdated={onReconciliationUpdated}
      />
      <AddReconciliationDialog
        open={dialog?.type === "add-reconciliation"}
        onClose={(dialog) => setDialog(dialog ? { type: dialog } : null)}
        bankAccount={bankAccount}
        onReconciliationUpdated={onReconciliationUpdated}
      />
      <ManualReconciliationDialog
        open={dialog?.type === "manual-reconciliation"}
        onClose={() => setDialog(null)}
        bankAccount={bankAccount}
        reconciliationIndex={
          dialog?.type === "manual-reconciliation" ? dialog.index : undefined
        }
        onReconciliationUpdated={onReconciliationUpdated}
      />
      <EditReconciliationDialog
        reconciliationIndex={
          dialog?.type === "edit-reconciliation" ? dialog.index : undefined
        }
        onClose={(dialog) => setDialog(dialog || null)}
        bankAccount={bankAccount}
        onReconciliationUpdated={onReconciliationUpdated}
      />
    </>
  );
}

export default BankAccount;
